import type {ComponentProps, FocusEvent, MutableRefObject} from 'react';
import {useMemo, useRef, useState} from 'react';

import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

import type {Item} from './typedef';


const getSortedItemsByDOMOrder = (items: MutableRefObject<Set<Item>>): Item[] => {
    const itemsArray = Array.from(items.current);
    return itemsArray.sort((a, b) => {
        const aElement = a.ref.current;
        const bElement = b.ref.current;

        if (!aElement || !bElement) {
            return 0;
        }

        // Use compareDocumentPosition to determine DOM order
        const position = aElement.compareDocumentPosition(bElement);

        if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
            return -1; // a comes before b
        } else if (position & Node.DOCUMENT_POSITION_PRECEDING) {
            return 1; // a comes after b
        }

        return 0;
    });
};


export type ArrowNavigationProps = {
    /**
     * When `true` (default), focuses the first DOM-ordered item whenever the
     * user tabs away from the navigation group. This ensures that the next
     * Tab-in always lands on the first item rather than whichever item happened
     * to be active last. Set to `false` to preserve the last active item across
     * focus-out/focus-in cycles (useful for persistent toolbar selections).
     */
    restoreOnBlur?: boolean,
};

/**
 * Manages keyboard arrow-key navigation across a flat list of registered items
 * (e.g. toolbar buttons, tab strips, radio groups).
 *
 * Items are sorted by their actual DOM position rather than registration order,
 * so rendering order and keyboard order always stay in sync regardless of when
 * each item mounts. Navigation wraps around: pressing ArrowRight on the last
 * item moves focus to the first, and vice versa.
 *
 * ## Integration
 * 1. Spread `getParentProps()` on the container element to attach the keydown,
 *    focus, and blur handlers.
 * 2. Provide `context` via `ArrowNavigationContext.Provider` so child items can
 *    register themselves.
 * 3. Each navigable item should call `useArrowNavigationItem()` which handles
 *    registration and the `setPointed` callback.
 *
 * ## Keyboard behaviour
 * - `ArrowRight` / `ArrowDown` — move focus to the next item (wraps).
 * - `ArrowLeft` / `ArrowUp` — move focus to the previous item (wraps).
 *
 * @param options.restoreOnBlur - When `true` (default), focus visually returns
 *   to the first DOM-ordered item when focus leaves the navigation container,
 *   ensuring subsequent Tab-ins land predictably.
 * @returns An object with `getParentProps()` and `context` to distribute via
 *   `ArrowNavigationContext`.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-arrow-navigation--documentation}
 */
export const useArrowNavigation = ({restoreOnBlur = true}: ArrowNavigationProps = {}) => {
    const items = useRef<Set<Item>>(new Set());
    const focusedItem = useRef<Item | null>(null);
    const [isOnFocus, setIsOnFocus] = useState(false);

    const handleFocus = (e: FocusEvent<HTMLDivElement>) => {
        setIsOnFocus(true);

        const focusedDOMNode = e.target as HTMLElement;
        const targetItem = Array.from(items.current).find(item => item.ref.current === focusedDOMNode);
        if (targetItem && focusedItem.current !== targetItem) {
            focusedItem.current?.setPointed(false);
            targetItem.setPointed(true);
            focusedItem.current = targetItem;
        }
    };

    const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
        if (e.relatedTarget && Array.from(items.current).some(item => item.ref.current === e.relatedTarget)) {
            return;
        }
        setIsOnFocus(false);
        if (restoreOnBlur) {
            focusedItem.current?.setPointed(false);
            const firstItem = getSortedItemsByDOMOrder(items)[0];
            focusedItem.current = firstItem;
            firstItem?.setPointed(true);
        }
    };

    const setActiveItem = (item: Item) => {
        focusedItem.current?.setPointed(false);
        item.setPointed(true);
        focusedItem.current = item;
    };

    const getNextItem = (currentItem: Item | null, direction: 'right' | 'left'): Item | null => {
        if (!items.current.size) {
            return null;
        }
        if (!currentItem) {
            return getSortedItemsByDOMOrder(items)[0];
        }
        const itemsArray = getSortedItemsByDOMOrder(items);
        const currentIndex = itemsArray.indexOf(currentItem);
        let newIndex = 0;
        if (direction === 'right') {
            newIndex = (currentIndex + 1) >= itemsArray.length ? 0 : currentIndex + 1;
        } else {
            newIndex = (currentIndex - 1) < 0 ? itemsArray.length - 1 : currentIndex - 1;
        }

        return itemsArray[newIndex];
    };

    const moveFocus = useCallbackWithRef((direction: 'right' | 'left') => {
        focusedItem.current?.setPointed(false);
        const nextItem = getNextItem(focusedItem.current, direction);
        nextItem?.setPointed(true);
        if (isOnFocus) {
            nextItem?.ref.current?.focus();
        }
        focusedItem.current = nextItem;
    });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
        if (!isOnFocus) {
            return;
        }
        if (['ArrowRight', 'ArrowDown'].includes(event.key)) {
            event.preventDefault();
            moveFocus('right');
        } else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            moveFocus('left');
        }
    };

    const context = useMemo(() => ({
        addItem: (item: Item) => {
            items.current.add(item);
            if (items.current.size === 1) {
                focusedItem.current = item;
                item.setPointed(true);
            }
        },
        removeItem: (item: Item) => {
            if (focusedItem.current === item && items.current.size > 1) {
                const sortedItems = getSortedItemsByDOMOrder(items);
                const currentIndex = sortedItems.indexOf(item);
                // If removing the first item, move focus right to the next item, otherwise left.
                moveFocus(currentIndex === 0 ? 'right' : 'left');
            }
            items.current.delete(item);
        },
        setActiveItem,
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        getParentProps: (props?: ComponentProps<'div'>): ComponentProps<'div'> => ({
            ...props,
            onKeyDown: event => {
                handleKeyDown(event);
                props?.onKeyDown?.(event);
            },
            onFocus: event => {
                handleFocus(event);
                props?.onFocus?.(event);
            },
            onBlur: event => {
                handleBlur(event);
                props?.onBlur?.(event);
            },
        }),
        context,
    };
};
